import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { TotoAdaptiveDock } from "@/components/toto/TotoAdaptiveSurface";
import "@/components/toto/today-layout.css";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, ExternalLink, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import "./zenoti-connection.css";

const DEFAULT_URL = "https://thenow.zenoti.com";
const BOUTIQUE_ATTENTION = {
  PLT: { rgb: "92 134 112", field: "231 240 234" },
  FLL: { rgb: "83 119 155", field: "229 237 244" },
  WYN: { rgb: "176 91 124", field: "245 232 238" },
  PNC: { rgb: "190 111 54", field: "246 235 224" },
  RNO: { rgb: "194 155 49", field: "247 240 213" },
  CC: { rgb: "170 63 63", field: "244 228 228" },
  RNB: { rgb: "116 112 124", field: "237 234 240" },
};

export default function ZenotiConnection({ embedded = false }) {
  const { user } = useAuth();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [roomActive, setRoomActive] = useState(false);
  const [roomUrl, setRoomUrl] = useState("");
  const [roomNotice, setRoomNotice] = useState("");
  const [capturedReports, setCapturedReports] = useState([]);
  const [captureErrors, setCaptureErrors] = useState([]);
  const [reportDiagnostic, setReportDiagnostic] = useState(null);
  const [collectingCenter, setCollectingCenter] = useState("");

  const connection = useMemo(
    () => connections.find((item) => item.provider === "zenoti"),
    [connections]
  );
  const attentionIdentity = BOUTIQUE_ATTENTION[user?.boutique_code] || BOUTIQUE_ATTENTION.RNB;
  const readinessStyle = {
    "--zenoti-team-rgb": attentionIdentity.rgb,
    "--zenoti-team-field-rgb": attentionIdentity.field,
  };

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.post("/connections/me/zenoti");
      const zenoti = response.data;
      setConnections([zenoti]);
      const setup = await api.get(`/connections/${zenoti.id}/setup`);
      setReadiness(setup.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load secure connections.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetClosedRoom = () => {
    setRoomActive(false);
    setRoomUrl("");
    setRoomNotice("The secure browser closed. Open it again to continue. Your previously saved Zenoti session is unchanged.");
  };

  useEffect(() => {
    if (!roomActive) return undefined;
    let stopped = false;
    let timer;
    const controller = new AbortController();
    const refreshCapturedReports = async () => {
      try {
        const response = await fetch("/protected-browser/session/downloads", {
          credentials: "same-origin", signal: controller.signal,
        });
        if (stopped) return;
        if ([401, 403, 410].includes(response.status)) {
          resetClosedRoom();
          return;
        }
        if (response.ok) {
          const payload = await response.json();
          if (stopped) return;
          setCapturedReports(Array.isArray(payload) ? payload : (payload.artifacts || []));
          setCaptureErrors(Array.isArray(payload) ? [] : (payload.errors || []));
        }
      } catch {
        // A temporary network interruption does not prove the room is closed.
      }
      if (!stopped) timer = window.setTimeout(refreshCapturedReports, 3000);
    };
    refreshCapturedReports();
    return () => {
      stopped = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [roomActive]);

  const prepareAuthentication = async () => {
    const roomWindow = window.open("", "sonmi-zenoti-room");
    setWorking(true);
    try {
      if (connection.start_url !== DEFAULT_URL) {
        await api.put(`/connections/${connection.id}/start-url`, {
          start_url: DEFAULT_URL,
          reason: "Use The NOW Massage Zenoti tenant for protected payroll reporting",
        });
      }
      const response = await api.post(`/connections/${connection.id}/auth/prepare`);
      const exchange = await fetch(response.data.gateway_path, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authentication_ticket: response.data.authentication_ticket }),
      });
      if (!exchange.ok) {
        const payload = await exchange.json().catch(() => ({}));
        throw new Error(payload.detail || "The protected browser room could not be opened.");
      }
      const room = await exchange.json();
      setRoomNotice("");
      setRoomActive(true);
      setRoomUrl(room.room_url);
      if (roomWindow) roomWindow.location = room.room_url;
      if (room.session_restored) await load();
      toast.success(room.session_restored ? "Your saved Zenoti login was restored." : "Protected Zenoti room opened. Sign in there, then return here to save the session.");
    } catch (error) {
      if (roomWindow) roomWindow.close();
      toast.error(error.response?.data?.detail || error.message || "Authentication could not be prepared.");
    } finally {
      setWorking(false);
    }
  };

  const refreshConnection = async () => {
    setWorking(true);
    try {
      const prepared = await api.post(`/connections/${connection.id}/refresh/prepare`);
      const response = await fetch(prepared.data.gateway_path, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authentication_ticket: prepared.data.refresh_ticket }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.detail || "Zenoti could not be refreshed. Your saved session was preserved.");
      toast.success("Zenoti connection refreshed.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Zenoti could not be refreshed.");
    } finally {
      setWorking(false);
    }
  };

  const openProtectedWorkspace = async () => {
    const roomWindow = window.open("", "sonmi-zenoti-room");
    setWorking(true);
    try {
      const response = await api.post(`/connections/${connection.id}/workspace/prepare`);
      const exchange = await fetch(response.data.gateway_path, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authentication_ticket: response.data.workspace_ticket }),
      });
      if (!exchange.ok) {
        const payload = await exchange.json().catch(() => ({}));
        throw new Error(payload.detail || "The protected Zenoti workspace could not be opened.");
      }
      const room = await exchange.json();
      setRoomNotice("");
      setRoomActive(true);
      setRoomUrl(room.room_url);
      if (roomWindow) roomWindow.location = room.room_url;
      toast.success("Protected Zenoti workspace opened with the encrypted session.");
    } catch (error) {
      if (roomWindow) roomWindow.close();
      toast.error(error.response?.data?.detail || error.message || "The protected workspace could not be opened.");
    } finally {
      setWorking(false);
    }
  };

  const openEGConversion = async () => {
    const roomWindow = window.open(roomUrl, "sonmi-zenoti-room");
    setWorking(true);
    try {
      const response = await fetch("/protected-browser/session/eg-conversion", {
        method: "POST", credentials: "same-origin",
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || "The EG conversion report could not be opened.");
      }
      if (roomWindow) roomWindow.focus();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setWorking(false);
    }
  };

  const finishAuthentication = async () => {
    setWorking(true);
    try {
      const response = await fetch("/protected-browser/session/complete", {
        method: "POST", credentials: "same-origin",
      });
      if ([401, 403, 410].includes(response.status)) {
        resetClosedRoom();
        return;
      }
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || "The protected session could not be saved.");
      }
      setRoomActive(false);
      setRoomUrl("");
      toast.success("The encrypted Zenoti browser session is connected.");
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setWorking(false);
    }
  };

  const inspectMembershipReport = async () => {
    setWorking(true);
    try {
      const navigation = await fetch(
        "/protected-browser/session/navigate?path=%2FAdmin%2FReports%2FReportData.aspx%3FReport%3Dmemberships_payment",
        { credentials: "same-origin" }
      );
      if (!navigation.ok) throw new Error("The protected report could not be opened.");
      const controls = await fetch("/protected-browser/session/report-controls", {
        credentials: "same-origin",
      });
      if (!controls.ok) throw new Error("The report controls could not be inspected.");
      setReportDiagnostic(await controls.json());
    } catch (error) {
      toast.error(error.message);
    } finally {
      setWorking(false);
    }
  };

  const collectMembershipPayment = async (center) => {
    setCollectingCenter(center);
    try {
      const response = await fetch("/protected-browser/session/collect-memberships-payment", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          center,
          start_date: "Aug 01, 2026",
          end_date: "Aug 18, 2026",
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = typeof payload.detail === "string"
          ? payload.detail
          : payload.detail?.message;
        throw new Error(detail || `The ${center} report could not be collected.`);
      }
      setCapturedReports((current) => [
        ...current.filter((report) => report.id !== payload.id),
        payload,
      ]);
      toast.success(`${center} Memberships Payment was collected and retained securely.`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCollectingCenter("");
    }
  };

  const cancelAuthentication = async () => {
    setWorking(true);
    try {
      await fetch("/protected-browser/session/cancel", { method: "POST", credentials: "same-origin" });
      setRoomActive(false);
      setRoomUrl("");
      await load();
    } finally {
      setWorking(false);
    }
  };

  const body = <>
        <div className="zenoti-connection__content" aria-busy={loading}>
        {loading ? <p role="status">Checking connection…</p> : <>
        {!connection ? (
          <section className="zenoti-connection__section"><p>Your Zenoti connection could not be loaded.</p><button type="button" onClick={load}>Try Again</button></section>
        ) : (
          <>
            <section className="zenoti-connection__section">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={18} className="text-emerald-700" />
                    <h2 className="font-serif-display text-2xl text-stone-900">{connection.display_name}</h2>
                  </div>
                  <p className="text-sm text-stone-500 mt-2">
                    {connection.organization_name} · {connection.authorized_locations.join(", ")}
                  </p>
                </div>
                <span className="px-3 py-1 rounded-full bg-stone-100 text-stone-600 text-xs">
                  {connection.status.replaceAll("_", " ")}
                </span>
              </div>
            </section>

            <section
              className={`zenoti-readiness-card zenoti-connection__section ${
                readiness?.browser_worker_ready ? "is-ready" : "needs-attention"
              }`}
              style={readinessStyle}
            >
              <details className="zenoti-connection__details"><summary>Connection Details</summary>
              <div className="space-y-3">
                <Requirement
                  ready={readiness?.requirements?.session_vault_key}
                  label="Encrypted session vault"
                  detail="Browser session state is encrypted outside GitHub and MongoDB."
                />
                <Requirement
                  ready={readiness?.requirements?.protected_browser_gateway}
                  label="Authenticated visual gateway"
                  detail="Access to the secure browser is checked before it opens."
                />
              </div></details>
              {roomNotice && <p role="status" className="mt-4 text-sm text-stone-700">{roomNotice}</p>}
              {!roomActive ? (
                <div className="zenoti-connection__actions"><button
                  type="button"
                  onClick={connection.status === "connected" ? openProtectedWorkspace : prepareAuthentication}
                  disabled={working || !readiness?.browser_worker_ready}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-900 text-white text-sm disabled:opacity-50"
                >
                  <ExternalLink size={15} />
                  {working
                    ? "Preparing…"
                    : readiness?.browser_worker_ready
                      ? connection.status === "connected"
                        ? "Open Secure Browser"
                        : ["attention_required", "expired"].includes(connection.status)
                          ? "Reconnect Zenoti"
                          : "Sign In to Zenoti"
                      : "Protected browser unavailable"}
                </button>
                {connection.status !== "disabled" && <button type="button" onClick={refreshConnection} disabled={working || !readiness?.browser_worker_ready}>Refresh connection</button>}
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={() => window.open(roomUrl, "sonmi-zenoti-room")} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full subtle-border text-stone-800 text-sm">
                    <ExternalLink size={15} /> Reopen Secure Browser
                  </button>
                  <button type="button" onClick={finishAuthentication} disabled={working} className="px-5 py-2.5 rounded-full bg-stone-900 text-white text-sm disabled:opacity-50">
                    {working ? "Saving…" : connection.status === "connected" ? "Save & Close" : "Save Session"}
                  </button>
                  <button type="button" onClick={openEGConversion} disabled={working} className="px-5 py-2.5 rounded-full subtle-border text-stone-800 text-sm disabled:opacity-50">
                    Open EG conversion report
                  </button>
                  <button type="button" onClick={cancelAuthentication} disabled={working} className="px-4 py-2.5 text-sm text-stone-500 disabled:opacity-50">Cancel</button>
                  {new URLSearchParams(window.location.search).has("diagnostic") && (
                    <>
                      <button type="button" onClick={inspectMembershipReport} disabled={working || Boolean(collectingCenter)} className="px-4 py-2.5 text-sm text-stone-500 disabled:opacity-50">
                        Inspect confirmed report
                      </button>
                      {["Plantation", "Ft. Lauderdale"].map((center) => (
                        <button
                          key={center}
                          type="button"
                          onClick={() => collectMembershipPayment(center)}
                          disabled={working || Boolean(collectingCenter)}
                          className="px-4 py-2.5 rounded-full subtle-border text-sm text-stone-700 disabled:opacity-50"
                        >
                          {collectingCenter === center ? `Collecting ${center}…` : `Collect ${center} membership payments`}
                        </button>
                      ))}
                    </>
                  )}
                  </div>
                  {reportDiagnostic && (
                    <pre data-testid="report-diagnostic" className="max-h-80 overflow-auto whitespace-pre-wrap text-xs text-stone-700">
                      {JSON.stringify(reportDiagnostic, null, 2)}
                    </pre>
                  )}
                  <div className="rounded-xl subtle-border bg-white/70 p-4" aria-live="polite">
                    <div className="text-xs uppercase tracking-wider text-stone-500">Captured Zenoti exports</div>
                    {capturedReports.length ? (
                      <ul className="mt-3 space-y-2">
                        {capturedReports.map((report) => (
                          <li key={report.id} className="flex flex-wrap items-center justify-between gap-3 text-sm">
                            <span className="text-stone-800">
                              {report.center ? `${report.center} · ` : ""}{report.source || report.filename}
                              {report.evidence_window && <span className="block text-xs text-stone-500 mt-1">{report.evidence_window}</span>}
                            </span>
                            <span className="text-stone-500">{Math.max(1, Math.round(report.size_bytes / 1024))} KB · retained securely</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-sm text-stone-500">No export has arrived from this workspace yet.</p>
                    )}
                    {captureErrors.map((message) => (
                      <p key={message} className="mt-2 text-sm text-stone-600">{message}</p>
                    ))}
                  </div>
                </div>
              )}
              {!readiness?.browser_worker_ready && (
                <p className="zenoti-readiness-message mt-4 text-sm flex items-start gap-2" role="status">
                  <ShieldAlert size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                  Login remains closed until both protections are configured. No fallback exposes the browser session.
                </p>
              )}
            </section>
          </>
        )}
        </>}
        </div>
  </>;
  if (embedded) return <div className="zenoti-connection zenoti-connection--embedded">{body}</div>;
  return (
    <main className="today-layout zenoti-connection">
      <section className="today-layout__vessel" aria-labelledby="zenoti-connection-title">
        <div className="today-layout__heading"><header><h1 id="zenoti-connection-title">Zenoti Connection</h1></header></div>
        <nav className="zenoti-connection__navigation" aria-label="Profile navigation"><Link to="/profile">My Profile</Link><span aria-current="page">Zenoti Connection</span></nav>
        {body}
      </section>
      <TotoAdaptiveDock active="profile" destinations={[]}/>
    </main>
  );
}

function Requirement({ ready, label, detail }) {
  return <div className="zenoti-requirement">
    {ready ? <CheckCircle2 size={18} className="zenoti-requirement-icon" aria-hidden="true"/> : <ShieldAlert size={18} aria-hidden="true"/>}
    <div><div className="text-sm">{label}</div><div className="text-xs text-stone-500 mt-1">{detail}</div></div>
  </div>;
}
